import { validateQuizCases, type QuizCaseDescriptor } from '@quebon/shared-learning'

export const LC_QUIZ_TYPES = [
  'multi_choice',
  'boolean',
  'fill_blank',
  'short_answer',
  'select_text',
  'drag_text',
  'coloring',
  'counting',
  'image_select',
  'ordering',
  'pairing',
  'explanation',
  'inline',
] as const

type LcQuizType = (typeof LC_QUIZ_TYPES)[number]

const BASE_QUIZ_IDS: Record<LcQuizType, string | null> = {
  multi_choice: '19837706214260337',
  boolean: '5415003651285133',
  fill_blank: '21238150823153687',
  short_answer: '21238150823153687',
  select_text: '2718018757512453',
  drag_text: '8046920963605206',
  coloring: '16795729986487345',
  counting: '12732455874204045',
  image_select: '1254039175305218',
  ordering: '20498180186998395',
  pairing: '4392871645980679',
  explanation: '21042531187197062',
  inline: '15627877785538888',
}

const BASE_AUDIT_TAGS: Partial<Record<LcQuizType, string[]>> = {
  multi_choice: ['subtype-c', 'media-image'],
  fill_blank: ['subtype-c'],
  short_answer: ['alias-fill-blank', 'subtype-c'],
  select_text: ['media-image', 'media-image-mobile'],
  counting: ['media-image'],
  image_select: ['media-image', 'media-image-mobile'],
  inline: ['children'],
}

const baseCases: QuizCaseDescriptor[] = LC_QUIZ_TYPES.map((type) => {
  const apiQuizId = BASE_QUIZ_IDS[type]

  return {
    id: `type.${type}.base`,
    titleKey: 'quizCatalog.caseTitles.base',
    expectedKey: 'quizCatalog.expectations.base',
    declaredType: type,
    rendererType: type,
    apiQuizId,
    family: 'quiz-renderer',
    renderStatus: apiQuizId ? 'renderable' : 'missing-data',
    mode: 'student',
    variant: 'lc',
    answerState: 'empty',
    resultState: 'none',
    contextKey: 'quizCatalog.contexts.regularLesson',
    tags: ['base', ...(BASE_AUDIT_TAGS[type] ?? [])],
    codeRefs: ['packages/shared-learning/src/components/quiz-types/quiz-renderer.tsx'],
  }
})

const dimCases: QuizCaseDescriptor[] = baseCases.flatMap((baseCase) => {
  const expectedTag = ['multi_choice', 'inline'].includes(baseCase.declaredType)
    ? 'dim-works'
    : 'dim-ignored'

  return [
    {
      ...baseCase,
      id: `type.${baseCase.declaredType}.dim-off`,
      titleKey: 'quizCatalog.caseTitles.dimOff',
      expectedKey: 'quizCatalog.expectations.dimOff',
      tags: ['dim-choice-text', expectedTag],
      rendererProps: { dimChoiceText: false },
    },
    {
      ...baseCase,
      id: `type.${baseCase.declaredType}.dim-on`,
      titleKey: 'quizCatalog.caseTitles.dimOn',
      expectedKey: 'quizCatalog.expectations.dimOn',
      tags: ['dim-choice-text', expectedTag],
      rendererProps: { dimChoiceText: true },
    },
  ]
})

const printCases: QuizCaseDescriptor[] = baseCases.flatMap((baseCase) => [
  {
    ...baseCase,
    id: `type.${baseCase.declaredType}.print-student`,
    titleKey: 'quizCatalog.caseTitles.printStudent',
    expectedKey: 'quizCatalog.expectations.printStudent',
    mode: 'print',
    variant: 'default',
    contextKey: 'quizCatalog.contexts.print',
    tags: ['print', 'student-copy'],
    rendererProps: { number: 1, compact: false },
  },
  {
    ...baseCase,
    id: `type.${baseCase.declaredType}.print-answer`,
    titleKey: 'quizCatalog.caseTitles.printAnswer',
    expectedKey: 'quizCatalog.expectations.printAnswer',
    mode: 'print',
    variant: 'default',
    contextKey: 'quizCatalog.contexts.print',
    tags: ['print', 'answer-key'],
    rendererProps: {
      number: 1,
      compact: false,
      showCorrectAnswers: true,
      correctAnswersSource: 'api',
    },
  },
])

function metadataCase(
  id: string,
  declaredType: string,
  resultState: QuizCaseDescriptor['resultState'],
  contextKey: string,
  expectedKey: string,
  tags: string[],
  codeRefs: string[],
): QuizCaseDescriptor {
  return {
    id,
    titleKey: `quizCatalog.caseTitles.${id.replace(/\./g, '_')}`,
    expectedKey,
    declaredType,
    rendererType: declaredType,
    apiQuizId: null,
    family: 'production-meta',
    renderStatus: 'metadata-only',
    mode: id === 'meta.assessment.adult-warning' ? 'adult' : 'student',
    variant: id.includes('aiq') ? 'aiq' : 'lc',
    answerState: resultState === 'none' ? 'empty' : 'saved',
    resultState,
    contextKey,
    tags,
    codeRefs,
  }
}

const metadataCases: QuizCaseDescriptor[] = [
  metadataCase(
    'meta.explanation.pending',
    'explanation',
    'pending',
    'quizCatalog.contexts.lmsGrading',
    'quizCatalog.expectations.explanationPending',
    ['manual-grading', 'answer-result-split'],
    ['apps/lc/src/components/admin/coaching/component-details/parts/AdultQuestionView.tsx'],
  ),
  metadataCase(
    'meta.explanation.partial',
    'explanation',
    'partial',
    'quizCatalog.contexts.lmsGrading',
    'quizCatalog.expectations.explanationPartial',
    ['manual-grading'],
    ['apps/lc/src/components/admin/coaching/component-details/parts/AdultQuestionView.tsx'],
  ),
  metadataCase(
    'meta.explanation.correct',
    'explanation',
    'correct',
    'quizCatalog.contexts.lmsGrading',
    'quizCatalog.expectations.explanationCorrect',
    ['manual-grading'],
    ['apps/lc/src/components/admin/coaching/component-details/parts/AdultQuestionView.tsx'],
  ),
  metadataCase(
    'meta.explanation.incorrect',
    'explanation',
    'incorrect',
    'quizCatalog.contexts.lmsGrading',
    'quizCatalog.expectations.explanationIncorrect',
    ['manual-grading'],
    ['apps/lc/src/components/admin/coaching/component-details/parts/AdultQuestionView.tsx'],
  ),
  metadataCase(
    'meta.assessment.adult-warning',
    'multi_choice',
    'none',
    'quizCatalog.contexts.assessment',
    'quizCatalog.expectations.assessmentAdultWarning',
    ['assessment', 'invalid-combination', 'warning'],
    ['packages/shared-learning/src/components/quiz/QuizContainer.tsx'],
  ),
  metadataCase(
    'meta.chapter-cumulative.no-online-ui',
    'multi_choice',
    'none',
    'quizCatalog.contexts.chapterCumulative',
    'quizCatalog.expectations.noOnlineUi',
    ['chapter-test', 'cumulative-test', 'no-online-ui'],
    ['apps/lc/src/components/admin/coaching/TestCoachingDetail.tsx'],
  ),
  metadataCase(
    'meta.assessment.student-aiq',
    'multi_choice',
    'none',
    'quizCatalog.contexts.assessment',
    'quizCatalog.expectations.assessmentStudentAiq',
    ['assessment', 'student', 'aiq', 'single-next'],
    ['apps/lc/src/pages/learner/diagnostic/DiagnosticAiq.tsx'],
  ),
  metadataCase(
    'meta.assessment.student-lc',
    'multi_choice',
    'none',
    'quizCatalog.contexts.assessment',
    'quizCatalog.expectations.assessmentStudentLc',
    ['assessment', 'student', 'lc', 'single-next'],
    ['apps/lc/src/pages/learner/special-lecture/SpecialLectureOnline.tsx'],
  ),
  metadataCase(
    'meta.solve-mode.test',
    'multi_choice',
    'pending',
    'quizCatalog.contexts.regularLesson',
    'quizCatalog.expectations.solveModeTest',
    ['solve-mode-test', 'session-result-hidden'],
    ['apps/lc/src/components/learner/QuizRunner.tsx'],
  ),
  metadataCase(
    'meta.offline.saved-pending',
    'multi_choice',
    'pending',
    'quizCatalog.contexts.offlineInput',
    'quizCatalog.expectations.offlineSavedPending',
    ['offline-input', 'answer-result-split'],
    ['apps/lc/src/components/learner/QuizRunner.tsx'],
  ),
  metadataCase(
    'meta.pause.external-submit',
    'multi_choice',
    'none',
    'quizCatalog.contexts.videoPause',
    'quizCatalog.expectations.pauseExternalSubmit',
    ['video-pause', 'external-submit'],
    ['apps/lc/src/components/learner/pause/PauseSolvePanel.tsx'],
  ),
  metadataCase(
    'meta.fallback.api-error',
    'multi_choice',
    'none',
    'quizCatalog.contexts.catalogFallback',
    'quizCatalog.expectations.fallbackApiError',
    ['fallback', 'api-error'],
    ['packages/shared-learning/src/components/QuizTestPage/useQuizTest.ts'],
  ),
  metadataCase(
    'meta.fallback.type-mismatch',
    'multi_choice',
    'none',
    'quizCatalog.contexts.catalogFallback',
    'quizCatalog.expectations.fallbackTypeMismatch',
    ['fallback', 'type-mismatch'],
    ['packages/shared-learning/src/components/QuizTestPage/QuizTestPage.tsx'],
  ),
  metadataCase(
    'meta.fallback.unsupported-renderer',
    'multi_choice',
    'none',
    'quizCatalog.contexts.catalogFallback',
    'quizCatalog.expectations.fallbackUnsupportedRenderer',
    ['fallback', 'unsupported-renderer'],
    ['packages/shared-learning/src/components/quiz-types/quiz-renderer.tsx'],
  ),
  metadataCase(
    'meta.fallback.missing-answer',
    'multi_choice',
    'none',
    'quizCatalog.contexts.catalogFallback',
    'quizCatalog.expectations.fallbackMissingAnswer',
    ['fallback', 'missing-answer'],
    ['packages/shared-learning/src/components/QuizTestPage/QuizTestPage.tsx'],
  ),
]

function variantCase(
  baseCase: QuizCaseDescriptor,
  id: string,
  variant: QuizCaseDescriptor['variant'],
  expectedKey: string,
  tags: string[],
): QuizCaseDescriptor {
  return {
    ...baseCase,
    id,
    titleKey: `quizCatalog.caseTitles.${id.replace(/\./g, '_')}`,
    expectedKey,
    variant,
    tags,
  }
}

const multiChoiceBase = baseCases.find((quizCase) => quizCase.declaredType === 'multi_choice')!
const fillBlankBase = baseCases.find((quizCase) => quizCase.declaredType === 'fill_blank')!

const variantCases: QuizCaseDescriptor[] = [
  variantCase(
    multiChoiceBase,
    'type.multi_choice.variant-default',
    'default',
    'quizCatalog.expectations.variantDefault',
    ['variant', 'current-test-page'],
  ),
  variantCase(
    multiChoiceBase,
    'type.multi_choice.variant-aiq',
    'aiq',
    'quizCatalog.expectations.variantAiq',
    ['variant', 'aiq'],
  ),
  variantCase(
    fillBlankBase,
    'type.fill_blank.variant-aiq-keypad',
    'aiq',
    'quizCatalog.expectations.variantAiqKeypad',
    ['variant', 'aiq', 'keypad'],
  ),
]

const diaryCases: QuizCaseDescriptor[] = [
  ['multi_choice', '11871960040476691'],
  ['fill_blank', '19071380608728267'],
  ['select_text', '17288276668220516'],
].map(([type, apiQuizId]) => ({
  id: `diary.${type}`,
  titleKey: 'quizCatalog.caseTitles.diaryFamily',
  expectedKey: 'quizCatalog.expectations.diaryFamily',
  declaredType: type,
  rendererType: type,
  apiQuizId,
  family: 'diary',
  renderStatus: 'metadata-only',
  mode: 'student',
  variant: 'default',
  answerState: 'empty',
  resultState: 'none',
  contextKey: 'quizCatalog.contexts.diary',
  tags: ['diary', 'quiz-id-allowlist', 'not-regular-lc'],
  codeRefs: ['packages/shared-learning/src/components/QuizTestPage/constants.ts'],
}))

export const LC_QUIZ_CASES = validateQuizCases([
  ...baseCases,
  ...dimCases,
  ...printCases,
  ...variantCases,
  ...diaryCases,
  ...metadataCases,
])
